import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import type { ConnectivityStatus } from '../hooks/useConnectivity';

// Per-status chip colors. The chip is fully filled with the status color so
// it reads at a glance: green = ok, amber = degraded, red = offline, grey =
// checking. Text color is chosen for contrast against each background.
const STATUS_STYLE: Record<ConnectivityStatus, { bg: string; fg: string }> = {
  connected: { bg: 'var(--caro-success)', fg: 'white' },
  degraded: { bg: 'var(--caro-warning)', fg: 'rgba(0,0,0,0.85)' },
  disconnected: { bg: 'var(--caro-emergency)', fg: 'white' },
  checking: { bg: 'var(--caro-text-light)', fg: 'white' },
};

interface StatusIndicatorProps {
  status: ConnectivityStatus;
}

export function StatusIndicator({ status }: StatusIndicatorProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { bg, fg } = STATUS_STYLE[status];

  const label = {
    connected: t.connectivity.allConnected,
    degraded: t.connectivity.limited,
    disconnected: t.connectivity.offline,
    checking: t.connectivity.checking,
  }[status];

  return (
    <button
      onClick={() => navigate('/connectivity')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: bg,
        color: fg,
        border: 'none',
        padding: '8px 16px',
        borderRadius: '999px',
        cursor: 'pointer',
        fontSize: 'var(--font-body)',
        fontWeight: 700,
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
        minHeight: 'auto',
        minWidth: 'auto',
      }}
      role="status"
      aria-label={label}
    >
      <span
        style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: fg,
          opacity: 0.9,
          flexShrink: 0,
        }}
      />
      {label}
    </button>
  );
}
